import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArticleSchema, BreadcrumbSchema } from '../components/StructuredData';
import { getNewsSessionId } from '../lib/news_session';
import { prefetchRoute } from '../lib/route_prefetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('he-IL', { year: 'numeric', month: 'long', day: 'numeric' }); }
  catch { return ''; }
};

/* WhatsApp share — viral distribution in groups.
 * NOTE: uses onClick + window.open() instead of `href` because the global
 * `a[href*="emergent"]` CSS rule (in public/index.html, intended for the
 * Emergent badge) would otherwise hide this button whenever the share URL
 * embeds the article's *.emergentagent.com origin. */
const WhatsAppShareButton = ({ url, text }) => {
  const handleClick = () => {
    const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
    const message = `${text}\n\n${origin}${url}`;
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 md:gap-2 bg-[#25D366] hover:bg-[#1ebe5d] active:bg-[#179a4f] text-white text-xs md:text-sm font-bold px-3 py-1.5 md:px-4 md:py-2 rounded-full transition-all hover:scale-105 hover:shadow-lg shadow-[#25D366]/30"
      data-testid="whatsapp-share-btn"
      aria-label="שתף בוואטסאפ"
    >
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 md:w-4 md:h-4" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
      </svg>
      <span>שתף בוואטסאפ</span>
    </button>
  );
};

/* Build a polished sticky CTA bar shown after the user scrolls past the first 40% of the article. */
const StickyCta = ({ url, text, onClick, visible }) => {
  if (!visible) return null;
  return (
    <div className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-[#0a0a14]/95 backdrop-blur-md border-t border-[#C5366A]/30 p-3 animate-in slide-in-from-bottom" dir="rtl">
      <a href={url} onClick={onClick} className="block w-full bg-[#C5366A] hover:bg-[#a82d57] text-white text-center py-3 rounded-xl font-black text-base transition-colors" data-testid="sticky-cta">
        {text}
      </a>
    </div>
  );
};

const NewsArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const contentRef = useRef(null);
  const scroll50Sent = useRef(false);
  const ctaSent = useRef(false);
  const [stickyVisible, setStickyVisible] = useState(false);

  /* Fetch article + register impression */
  useEffect(() => {
    const sid = getNewsSessionId();
    fetch(`${API_URL}/api/news/articles/${encodeURIComponent(slug)}?session_id=${encodeURIComponent(sid)}`)
      .then(async (r) => {
        if (!r.ok) { setErr(r.status === 404 ? 'הכתבה לא נמצאה' : 'שגיאה בטעינת הכתבה'); return null; }
        return r.json();
      })
      .then((d) => { if (d) setArticle(d); })
      .catch(() => setErr('שגיאה בטעינת הכתבה'))
      .finally(() => setLoading(false));
  }, [slug]);

  /* Scroll-50 tracking */
  useEffect(() => {
    if (!article) return;
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const articleTopAbs = rect.top + window.scrollY;
      const articleHeight = el.offsetHeight;
      const viewportTopRelArticle = window.scrollY - articleTopAbs;
      const ratio = viewportTopRelArticle / Math.max(1, articleHeight - window.innerHeight);

      if (!stickyVisible && ratio > 0.15) setStickyVisible(true);

      if (!scroll50Sent.current && ratio >= 0.5) {
        scroll50Sent.current = true;
        fetch(`${API_URL}/api/news/track/scroll-50`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: article.session_id, article_id: article.id }),
        }).catch(() => {});
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [article, stickyVisible]);

  const trackCtaClick = () => {
    if (!article || ctaSent.current) return;
    ctaSent.current = true;
    fetch(`${API_URL}/api/news/track/cta-click`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: article.session_id, article_id: article.id }),
    }).catch(() => {});
  };

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#C5366A] animate-spin" />
    </div>
  );

  if (err || !article) return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-20 text-center" dir="rtl">
        <h1 className="text-2xl font-bold text-gray-700 mb-3">{err || 'הכתבה לא נמצאה'}</h1>
        <Link to="/blog/news" className="inline-flex items-center gap-2 text-[#C5366A] hover:underline font-bold">
          <ArrowLeft className="w-4 h-4" />חזרה לחדשות
        </Link>
      </main>
      <Footer />
    </div>
  );

  const cleanBody = DOMPurify.sanitize(article.body_html || '');
  const images = (article.images || []).filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <SEO title={article.headline || article.primary_title} description={article.subtitle || ''}
        path={`/blog/news/${article.slug}`} image={article.cover_image || undefined} type="article" />
      <ArticleSchema article={article} kind="NewsArticle" path={`/blog/news/${article.slug}`} />
      <BreadcrumbSchema items={[
        { name: 'ראשי', path: '/' },
        { name: 'בלוג', path: '/blog' },
        { name: 'חדשות', path: '/blog/news' },
        { name: article.headline || article.primary_title || '', path: `/blog/news/${article.slug}` },
      ]} />
      <Header />

      <main id="main-content" tabIndex={-1} dir="rtl">
        <article ref={contentRef} className="max-w-3xl mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-8 md:pb-12" data-testid="news-article">
          {/* Breadcrumb + share */}
          <nav className="mb-5 flex items-center justify-between gap-3 flex-wrap" aria-label="Breadcrumb">
            <div className="text-xs md:text-sm">
              <Link to="/blog/news" className="text-[#C5366A] hover:underline font-medium">חדשות</Link>
              <span className="text-gray-400 mx-2">/</span>
              <span className="text-gray-500">{fmtDate(article.created_at)}</span>
            </div>
            <WhatsAppShareButton url={`/blog/news/${article.slug}`} text={article.headline || article.primary_title} />
          </nav>

          {/* Headline (variant) */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-[#0a0a14] leading-[1.15] mb-3" data-testid="article-headline">
            {article.headline}
          </h1>

          {/* Subtitle (variant) */}
          {article.subtitle && (
            <p className="text-lg md:text-xl text-gray-600 leading-snug mb-6 border-r-4 border-[#C5366A] pr-4" data-testid="article-subtitle">
              {article.subtitle}
            </p>
          )}

          {/* Cover image */}
          {article.cover_image && (
            <div className="relative -mx-4 md:mx-0 mb-6 md:mb-8 aspect-[16/9] bg-gray-100 overflow-hidden md:rounded-lg" data-testid="article-cover">
              <img src={article.cover_image} alt={article.headline} className="w-full h-full object-cover" />
            </div>
          )}

          {/* Inline 1st image (if exists) shown between paragraphs of body */}
          <div className="news-body prose prose-lg max-w-none text-[#1a1a1a]"
            dangerouslySetInnerHTML={{ __html: cleanBody }} data-testid="article-body" />

          {/* Gallery (additional images shown after body) */}
          {images.length > 0 && (
            <div className={`mt-8 grid gap-3 ${images.length === 1 ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-3'}`} data-testid="article-gallery">
              {images.map((src, i) => (
                <div key={i} className="aspect-[4/3] bg-gray-100 overflow-hidden rounded-lg">
                  <img src={src} alt={`תמונה ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          )}

          {/* End-of-article yellow CTA stinger */}
          {(article.end_cta_text || article.end_cta_link_text) && (
            <div className="mt-10 mb-8 text-center" data-testid="article-end-cta">
              <p className="text-xl md:text-2xl leading-relaxed font-bold">
                {article.end_cta_text && (
                  <span className="bg-yellow-300 text-black px-2 rounded-md">{article.end_cta_text}</span>
                )}
                {article.end_cta_text && article.end_cta_link_text && ' '}
                {article.end_cta_link_text && (
                  <a href={article.end_cta_link_url || '#'} onClick={trackCtaClick}
                    className="bg-yellow-300 text-black px-2 rounded-md underline decoration-2 underline-offset-4 hover:bg-yellow-400 transition-colors font-black"
                    data-testid="article-end-cta-link">
                    {article.end_cta_link_text}
                  </a>
                )}
              </p>
            </div>
          )}

          {/* Main CTA button */}
          <div className="mt-8 md:mt-12 mb-6">
            <a href={article.cta_url} onClick={trackCtaClick}
              className="block w-full md:w-auto md:inline-flex bg-[#C5366A] hover:bg-[#a82d57] text-white text-center text-lg md:text-xl font-black px-8 md:px-12 py-4 md:py-5 rounded-xl transition-all hover:-translate-y-0.5 shadow-xl shadow-[#C5366A]/20 hover:shadow-[#C5366A]/40 items-center justify-center gap-2 group"
              data-testid="article-main-cta">
              <span>{article.cta_text}</span>
              <ArrowLeft className="w-5 h-5 transition-transform group-hover:-translate-x-1" />
            </a>
          </div>

          {/* Related articles (ranked by overall CR) */}
          {(article.related || []).length > 0 && (
            <section className="mt-16 md:mt-20 pt-8 md:pt-10 border-t-2 border-gray-200" data-testid="related-articles-section">
              <div className="flex items-center justify-between mb-5 md:mb-6">
                <h2 className="text-xl md:text-2xl font-black text-[#0a0a14]">
                  <span className="text-[#C5366A]">קרא גם:</span> כתבות נבחרות
                </h2>
                <Link to="/blog/news" className="text-[#C5366A] hover:underline text-sm font-bold inline-flex items-center gap-1">
                  כל החדשות <ArrowLeft className="w-4 h-4" />
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
                {(article.related || []).map((r) => (
                  <Link key={r.slug} to={`/blog/news/${r.slug}`} className="group block" data-testid={`related-card-${r.slug}`}>
                    {r.cover_image ? (
                      <div className="aspect-[16/10] overflow-hidden bg-gray-100 mb-3 rounded-lg">
                        <img src={r.cover_image} alt={r.display_headline} loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="aspect-[16/10] bg-gradient-to-br from-[#C5366A]/10 to-[#FDF8FA] mb-3 rounded-lg flex items-center justify-center">
                        <span className="text-[#C5366A] font-black text-3xl opacity-30">חדשות</span>
                      </div>
                    )}
                    <h3 className="text-base md:text-lg font-black text-[#0a0a14] leading-snug group-hover:text-[#C5366A] transition-colors mb-1.5">
                      {r.display_headline}
                    </h3>
                    {r.display_subtitle && (
                      <p className="text-sm text-gray-600 leading-snug line-clamp-2">
                        {r.display_subtitle}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <StickyCta url={article.cta_url} text={article.cta_text} onClick={trackCtaClick} visible={stickyVisible} />
      <Footer />

      <style>{`
        .news-body { font-family: inherit; line-height: 1.85; font-size: 1.0625rem; }
        .news-body h1 { font-size: 1.75rem; font-weight: 800; margin: 1.6em 0 0.5em; color: #0a0a14; }
        .news-body h2 { font-size: 1.4rem; font-weight: 800; margin: 1.6em 0 0.4em; color: #0a0a14; border-right: 3px solid #C5366A; padding-right: 0.75em; }
        .news-body p { margin: 0.95em 0; color: #1a1a1a; }
        .news-body a { color: #C5366A; text-decoration: underline; font-weight: 600; }
        .news-body ul, .news-body ol { padding-right: 1.5em; margin: 0.9em 0; }
        .news-body blockquote { border-right: 4px solid #C5366A; background: #FDF8FA; padding: 1em 1.25em; margin: 1.2em 0; font-style: italic; color: #2a2a2a; }
        .news-body strong { font-weight: 800; }
      `}</style>
    </div>
  );
};

export default NewsArticlePage;
